import { useState } from "react";
import { useApi, useFetcher, type ApiToken, type AuditEntry, type GitHubStatus, type Member, type Organisation, type Page, type Runner } from "../api.js";
import { useOrg } from "../components/Layout.js";
import { Button, Card, Empty, ErrorBox, Loading, PageHeader, StatusBadge, Time, useAction } from "../components/ui.js";

export function RunnersPage() {
  const org = useOrg();
  const fetcher = useFetcher();
  const runners = useApi<Page<Runner>>(`/v1/orgs/${org}/runners`, { refreshMs: 15_000 });
  const [showRetired, setShowRetired] = useState(false);
  const [registration, setRegistration] = useState<{ token: string; expiresAt: string }>();
  const register = useAction(async (ephemeral: boolean) => {
    setRegistration(await fetcher(`/v1/orgs/${org}/runners/registrations`, { method: "POST", body: { ephemeral } }));
  });
  const retire = useAction((id: string) => fetcher(`/v1/orgs/${org}/runners/${id}`, { method: "DELETE" }), () => {
    runners.reload();
  });
  const apiUrl = `${window.location.origin}/api`;
  const all = runners.data?.items ?? [];
  const retiredCount = all.filter((r) => r.status === "retired").length;
  const visible = showRetired ? all : all.filter((r) => r.status !== "retired");
  return (
    <>
      <PageHeader
        title="Runners"
        subtitle="Machines that execute jobs for this organisation"
        actions={
          <Button variant="primary" busy={register.busy} onClick={() => { void register.run(false); }}>
            Add runner
          </Button>
        }
      />
      <ErrorBox error={runners.error ?? register.error ?? retire.error} />
      {registration && (
        <Card title="Register a runner">
          <p className="muted">
            Run on the machine, from a checkout of REPU-ME/forge (Node.js 24). The token works once and expires <Time iso={registration.expiresAt} />.
          </p>
          <pre className="code">{`git clone https://github.com/REPU-ME/forge.git forge && cd forge
pnpm install && pnpm --filter "@repu/runner..." build
node apps/runner/dist/cli.js register --api-url ${apiUrl} --token ${registration.token}
node apps/runner/dist/cli.js run`}</pre>
          <p className="muted small">
            Runners take jobs for their own OS. Web, functions and database jobs need Linux; to run them on a Mac, register with{" "}
            <code>REPU_RUNNER_OS=linux</code> in front of the register command. Add <code>--labels a,b</code> for jobs that ask for labels.
          </p>
        </Card>
      )}
      {runners.loading && <Loading />}
      {retiredCount > 0 && (
        <label className="muted small">
          <input type="checkbox" checked={showRetired} onChange={(e) => { setShowRetired(e.target.checked); }} /> Show retired runners ({retiredCount})
        </label>
      )}
      {runners.data && visible.length === 0 && <Empty title="No runners">Add a runner, or configure GitHub Actions runners in Settings.</Empty>}
      {runners.data && visible.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Platform</th>
              <th>Labels</th>
              <th>Pool</th>
              <th>Last seen</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td>
                  <StatusBadge status={r.status} />
                </td>
                <td>
                  {r.os} {r.arch}
                </td>
                <td className="muted">{r.labels.join(", ") || "—"}</td>
                <td>{r.pool}</td>
                <td>
                  <Time iso={r.lastSeenAt} />
                </td>
                <td>
                  {r.status !== "retired" && (
                    <button className="link-button danger" onClick={() => { if (window.confirm(`Retire ${r.name}?`)) void retire.run(r.id); }}>
                      Retire
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export function AuditPage() {
  const org = useOrg();
  const [cursor, setCursor] = useState<string>();
  const audit = useApi<Page<AuditEntry>>(`/v1/orgs/${org}/audit?limit=50${cursor ? `&cursor=${cursor}` : ""}`);
  return (
    <>
      <PageHeader title="Audit log" subtitle="Security-relevant actions in this organisation" />
      <ErrorBox error={audit.error} />
      {audit.loading && <Loading />}
      {audit.data && (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Outcome</th>
                <th>Actor</th>
                <th>Resource</th>
              </tr>
            </thead>
            <tbody>
              {audit.data.items.map((a) => (
                <tr key={a.id}>
                  <td>
                    <Time iso={a.at} />
                  </td>
                  <td>
                    <code>{a.action}</code>
                  </td>
                  <td>
                    <StatusBadge status={a.outcome === "success" ? "succeeded" : a.outcome} />
                  </td>
                  <td className="muted">{a.actor.userId ?? a.actor.event ?? a.actor.type}</td>
                  <td className="muted">
                    {a.resource.type} {a.resource.id}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {audit.data.nextCursor && (
            <Button onClick={() => { setCursor(audit.data?.nextCursor ?? undefined); }}>Older</Button>
          )}
        </>
      )}
    </>
  );
}

const ROLES = ["viewer", "developer", "releaser", "admin", "owner"] as const;

export function SettingsPage() {
  return (
    <>
      <PageHeader title="Settings" />
      <GitHubSettings />
      <UsageSettings />
      <MembersSettings />
      <TokensSettings />
    </>
  );
}

function GitHubSettings() {
  const org = useOrg();
  const fetcher = useFetcher();
  const status = useApi<GitHubStatus>(`/v1/orgs/${org}/github`);
  const organisation = useApi<Organisation & { settings?: unknown }>(`/v1/orgs/${org}`);
  const [installationId, setInstallationId] = useState("");
  const [runnerRepo, setRunnerRepo] = useState("");
  const [runnerOs, setRunnerOs] = useState<("linux" | "macos")[]>(["linux", "macos"]);
  const [runnerSaved, setRunnerSaved] = useState(false);
  const link = useAction(() => fetcher(`/v1/orgs/${org}/github/link`, { method: "POST", body: { installationId: Number(installationId) } }), () => {
    status.reload();
    organisation.reload();
  });
  const sync = useAction(() => fetcher(`/v1/orgs/${org}/github/sync`, { method: "POST" }), () => {
    status.reload();
  });
  const saveRunner = useAction(
    () => fetcher(`/v1/orgs/${org}/settings`, { method: "PUT", body: { githubActionsRunner: runnerRepo && runnerOs.length > 0 ? { repository: runnerRepo, os: runnerOs } : null } }),
    () => {
      setRunnerSaved(true);
    },
  );
  const toggleOs = (os: "linux" | "macos", on: boolean) => {
    setRunnerSaved(false);
    setRunnerOs((current) => (on ? [...new Set([...current, os])] : current.filter((o) => o !== os)));
  };
  return (
    <Card title="GitHub">
      <ErrorBox error={status.error ?? link.error ?? sync.error ?? saveRunner.error} />
      {status.data && !status.data.configured && <p className="muted">The GitHub App is not configured on this Forge instance.</p>}
      {status.data?.installation ? (
        <>
          <p>
            Linked to <strong>{status.data.installation.account}</strong> (installation {status.data.installation.id}){" "}
            {status.data.installation.deleted && <span className="tag warn">uninstalled</span>}
          </p>
          <p className="muted">{status.data.repositories.length} repositories mirrored.</p>
          <Button busy={sync.busy} onClick={() => { void sync.run(); }}>
            Sync repositories
          </Button>
        </>
      ) : status.data?.configured === true && (
        <form
          className="form inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            void link.run();
          }}
        >
          <p className="muted wide">Install the Forge GitHub App on your organisation, then enter the installation ID from the app's settings URL. You must be a GitHub owner of that account.</p>
          <label>
            Installation ID
            <input value={installationId} onChange={(e) => { setInstallationId(e.target.value); }} inputMode="numeric" pattern="[0-9]+" required />
          </label>
          <div className="form-actions">
            <Button variant="primary" type="submit" busy={link.busy}>
              Link installation
            </Button>
          </div>
        </form>
      )}
      <form
        className="form inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          void saveRunner.run();
        }}
      >
        <p className="muted wide">
          Jobs can run on GitHub-hosted runners: for each queued job, Forge dispatches the <code>repu-runner.yml</code> workflow in this repository.
          Needs the GitHub App linked above, with Actions (write) permission on the repository. Leave the repository empty to turn this off.
        </p>
        <label>
          Runner workflow repository
          <input value={runnerRepo} onChange={(e) => { setRunnerRepo(e.target.value); setRunnerSaved(false); }} placeholder="REPU-ME/forge" />
        </label>
        <fieldset className="checks">
          <legend>Run these jobs on GitHub</legend>
          <label>
            <input type="checkbox" checked={runnerOs.includes("linux")} onChange={(e) => { toggleOs("linux", e.target.checked); }} /> Linux (ubuntu-24.04)
          </label>
          <label>
            <input type="checkbox" checked={runnerOs.includes("macos")} onChange={(e) => { toggleOs("macos", e.target.checked); }} /> macOS (macos-15)
          </label>
        </fieldset>
        {runnerSaved && <p className="muted wide">{runnerRepo ? `Saved: ${runnerOs.join(" and ")} jobs dispatch to ${runnerRepo}.` : "Saved: GitHub-hosted runners are off."}</p>}
        <div className="form-actions">
          <Button type="submit" busy={saveRunner.busy}>
            Save
          </Button>
        </div>
      </form>
    </Card>
  );
}

interface UsageRow {
  projectId?: string;
  runnerOs?: string;
  jobs: number;
  minutes: number;
}

function UsageSettings() {
  const org = useOrg();
  const usage = useApi<{ since: string; items: UsageRow[] }>(`/v1/orgs/${org}/usage?by=projectId,runnerOs`);
  const projects = useApi<Page<{ id: string; slug: string }>>(`/v1/orgs/${org}/projects`);
  const slug = (id?: string) => projects.data?.items.find((p) => p.id === id)?.slug ?? id ?? "—";
  return (
    <Card title="Runner usage (30 days)">
      <ErrorBox error={usage.error} />
      {usage.data?.items.length === 0 && <p className="muted">No jobs have run yet.</p>}
      {usage.data && usage.data.items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Runner</th>
              <th>Jobs</th>
              <th>Minutes</th>
            </tr>
          </thead>
          <tbody>
            {usage.data.items.map((u) => (
              <tr key={`${u.projectId ?? ""}:${u.runnerOs ?? ""}`}>
                <td>{slug(u.projectId)}</td>
                <td>{u.runnerOs}</td>
                <td>{u.jobs}</td>
                <td>{u.minutes.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted small">macOS minutes on GitHub-hosted runners count 10× against GitHub's free allowance for private repositories.</p>
    </Card>
  );
}

function MembersSettings() {
  const org = useOrg();
  const fetcher = useFetcher();
  const members = useApi<Page<Member>>(`/v1/orgs/${org}/members`);
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<(typeof ROLES)[number]>("developer");
  const put = useAction((id: string, r: string) => fetcher(`/v1/orgs/${org}/members/${id}`, { method: "PUT", body: { role: r } }), () => {
    members.reload();
    setUserId("");
  });
  const remove = useAction((id: string) => fetcher(`/v1/orgs/${org}/members/${id}`, { method: "DELETE" }), () => {
    members.reload();
  });
  return (
    <Card title="Members">
      <ErrorBox error={members.error ?? put.error ?? remove.error} />
      <table className="table">
        <thead>
          <tr>
            <th>Member</th>
            <th>Role</th>
            <th>Scoped grants</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {members.data?.items.map((m) => (
            <tr key={m.userId}>
              <td>
                {m.name ?? m.userId} {m.githubLogin && <span className="muted">@{m.githubLogin}</span>}
              </td>
              <td>
                <select value={m.role} onChange={(e) => { void put.run(m.userId, e.target.value); }}>
                  {ROLES.map((r) => (
                    <option key={r}>{r}</option>
                  ))}
                </select>
              </td>
              <td className="muted">{m.grants.map((g) => `${g.role} on ${g.scope.type}`).join(", ") || "—"}</td>
              <td>
                <button className="link-button danger" onClick={() => { if (window.confirm("Remove this member?")) void remove.run(m.userId); }}>
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <form
        className="form inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          void put.run(userId, role);
        }}
      >
        <label>
          User ID
          <input value={userId} onChange={(e) => { setUserId(e.target.value); }} required placeholder="Signed-in user's ID" />
        </label>
        <label>
          Role
          <select value={role} onChange={(e) => { setRole(e.target.value as (typeof ROLES)[number]); }}>
            {ROLES.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </label>
        <div className="form-actions">
          <Button type="submit" busy={put.busy}>
            Add member
          </Button>
        </div>
        <p className="muted small wide">People must sign in to Forge once before they can be added.</p>
      </form>
    </Card>
  );
}

function TokensSettings() {
  const org = useOrg();
  const fetcher = useFetcher();
  const tokens = useApi<Page<ApiToken>>(`/v1/orgs/${org}/tokens`);
  const [name, setName] = useState("");
  const [created, setCreated] = useState<string>();
  const create = useAction(async () => {
    const t = await fetcher<ApiToken & { token: string }>(`/v1/orgs/${org}/tokens`, { method: "POST", body: { name, expiresInDays: 90 } });
    setCreated(t.token);
    setName("");
    tokens.reload();
  });
  const revoke = useAction((id: string) => fetcher(`/v1/orgs/${org}/tokens/${id}`, { method: "DELETE" }), () => {
    tokens.reload();
  });
  return (
    <Card title="Your API tokens">
      <ErrorBox error={tokens.error ?? create.error ?? revoke.error} />
      {created && (
        <div className="notice">
          Copy this token now; it will not be shown again:
          <pre className="code">{created}</pre>
        </div>
      )}
      {tokens.data && tokens.data.items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Token</th>
              <th>Last used</th>
              <th>Expires</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tokens.data.items.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td>
                  <code>{t.prefix}…</code>
                </td>
                <td>
                  <Time iso={t.lastUsedAt} />
                </td>
                <td>{new Date(t.expiresAt).toLocaleDateString()}</td>
                <td>
                  <button className="link-button danger" onClick={() => { void revoke.run(t.id); }}>
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form
        className="form inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          void create.run();
        }}
      >
        <label>
          Token name
          <input value={name} onChange={(e) => { setName(e.target.value); }} required placeholder="CI automation" />
        </label>
        <div className="form-actions">
          <Button type="submit" busy={create.busy}>
            Create token
          </Button>
        </div>
      </form>
    </Card>
  );
}

